import { FormEvent, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Form } from 'react-bootstrap';
import { GraduateCompletionForm, GraduateEditorApi, GraduationApi, SaveGraduateCompletion, errorMessage } from '../services/GraduationApi';

const capstoneTypes = [
  { id: 0, label: 'Thesis' }, { id: 1, label: 'Dissertation' },
  { id: 2, label: 'Alternate Plan Paper' }, { id: 3, label: 'Other' },
];

type Faculty = GraduateCompletionForm['faculty'];
type CompletionField = 'type' | 'title' | 'details' | 'advisor' | 'coordinator';
const completionFieldIds: Record<CompletionField, string> = { type: 'capstone-type', title: 'capstone-title', details: 'capstone-details',
  advisor: 'graduate-advisor', coordinator: 'graduate-coordinator' };
const facultyDisplayName = (person: Faculty[number]) =>
  person.departmentName?.trim() ? `${person.name} — ${person.departmentName.trim()}` : person.name;
const facultyLabel = (faculty: Faculty, person: Faculty[number]) => {
  const name = facultyDisplayName(person);
  return faculty.some(other => other.id !== person.id && facultyDisplayName(other) === name)
    ? `${name} (#${person.id})` : name;
};

function FacultyAutosuggest({ id, faculty, departmentId, selectedId, savedName, onSelect, error }: {
  id: string; faculty: Faculty; departmentId: string | null | undefined;
  selectedId: string; savedName: string | null; onSelect: (id: string) => void; error?: string;
}) {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState(() => {
    const selected = faculty.find(person => String(person.id) === selectedId);
    return selected ? facultyLabel(faculty, selected) : savedName || '';
  });
  const choices = faculty.map(person => ({ id: String(person.id), label: facultyLabel(faculty, person),
    departmentId: person.departmentId }));
  const suggestions = searching ? choices : choices.filter(person =>
    (departmentId && person.departmentId === departmentId) || person.id === selectedId);
  return <>
    <Form.Control list={`${id}-suggestions`} required autoComplete="off" aria-autocomplete="list"
      isInvalid={Boolean(error)} aria-invalid={Boolean(error)} aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
      value={query} onChange={event => {
        const value = event.target.value;
        setQuery(value); setSearching(true);
        onSelect(choices.find(person => person.label === value)?.id || '');
      }} />
    <datalist id={`${id}-suggestions`}>{suggestions.map(person =>
      <option key={person.id} value={person.label} />)}</datalist>
    <Form.Text id={`${id}-help`}>{departmentId ? 'Your department’s faculty appear first. Type a name to search the full graduate faculty roster.' :
      'Type a name to search the graduate faculty roster.'}</Form.Text>
    {error && <Form.Control.Feedback type="invalid" id={`${id}-error`}>{error}</Form.Control.Feedback>}
  </>;
}

export default function GraduateCapstoneApprovals({ api = GraduationApi, adminMode = false, applicationId, canWrite, startEditing = false, onDone, onEditApplication,
  onEditCourses }: {
  api?: GraduateEditorApi; adminMode?: boolean; applicationId: number; canWrite: boolean; startEditing?: boolean; onDone: (saved: boolean) => void;
  onEditApplication: () => void; onEditCourses?: () => void;
}) {
  const [form, setForm] = useState<GraduateCompletionForm | null>(null);
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [approval, setApproval] = useState('');
  const [advisor, setAdvisor] = useState('');
  const [coordinator, setCoordinator] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CompletionField, string>>>({});
  const completionForm = useRef<HTMLFormElement | null>(null);
  const invalidField = useRef<CompletionField | null>(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const pending = useRef<SaveGraduateCompletion | null>(null);
  const saving = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const capstoneTypeInput = useRef<HTMLSelectElement | null>(null);

  useEffect(() => {
    const abort = new AbortController(); controller.current = abort;
    setForm(null); setError(''); setFieldErrors({});
    api.graduateCompletion(applicationId, abort.signal).then(data => {
      setForm(data); setEditing(startEditing && canWrite && data.canEdit);
      setType(data.capstone?.type ?? null); setTitle(data.capstone?.title ?? '');
      setDetails(data.capstone?.details ?? ''); setApproval(data.capstone?.approvalNumber ?? '');
      setAdvisor(data.advisorFacultyId?.toString() ?? '');
      setCoordinator(data.coordinatorFacultyId?.toString() ?? '');
    }).catch(e => { if (!axios.isCancel(e)) setError(errorMessage(e)); });
    return () => abort.abort();
  }, [applicationId, canWrite, startEditing, retry, api]);
  useEffect(() => {
    if (invalidField.current) {
      completionForm.current?.querySelector<HTMLElement>(`#${completionFieldIds[invalidField.current]}`)?.focus();
      invalidField.current = null;
    }
  }, [fieldErrors]);
  const clearFieldError = (field: CompletionField) => setFieldErrors(current => ({ ...current, [field]: undefined }));

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!form || !canWrite || !form.canEdit || saving.current) return;
    const errors: Partial<Record<CompletionField, string>> = {};
    if (type === null) errors.type = 'Select a capstone type.';
    else if (type === 3 && !details.trim()) errors.details = 'Enter the other capstone type or details.';
    else if ((type === 1 || type === 2) && !title.trim()) errors.title = 'Enter the capstone title.';
    if (!advisor || (!(form.advisorSigned && String(form.advisorFacultyId) === advisor) && !form.faculty.some(person => String(person.id) === advisor)))
      errors.advisor = 'Select an Advisor / Committee Chair from the graduate faculty suggestions.';
    if (!coordinator || (!(form.coordinatorSigned && String(form.coordinatorFacultyId) === coordinator) && !form.faculty.some(person => String(person.id) === coordinator)))
      errors.coordinator = 'Select a Graduate Program Coordinator from the graduate faculty suggestions.';
    invalidField.current = (Object.keys(errors) as CompletionField[])[0] || null;
    setFieldErrors(errors);
    if (invalidField.current || type === null) {
      setError(Object.values(errors).join(' ')); return;
    }
    const draft: SaveGraduateCompletion = {
      capstoneType: type, title: type === 3 || type === 0 ? null : title.trim(),
      details: type === 3 ? details.trim() : null,
      approvalNumber: type <= 1 ? approval.trim() || null : null,
      advisorFacultyId: Number(advisor), coordinatorFacultyId: Number(coordinator),
      version: form.version, requestId: '',
    };
    const previous = pending.current;
    draft.requestId = previous && JSON.stringify({ ...previous, requestId: '' }) === JSON.stringify(draft)
      ? previous.requestId : crypto.randomUUID();
    pending.current = draft; saving.current = true; setBusy(true); setError('');
    try { await api.saveGraduateCompletion(applicationId, draft, controller.current?.signal); onDone(true); }
    catch (e) { if (!axios.isCancel(e)) setError(errorMessage(e)); }
    finally { saving.current = false; setBusy(false); }
  };

  const editable = canWrite && Boolean(form?.canEdit) && editing;
  useEffect(() => { if (editable) capstoneTypeInput.current?.focus(); }, [editable]);
  const facultyName = (id: number | null, savedName: string | null) =>
    form?.faculty.find(person => person.id === id)?.name || savedName || 'Not selected';
  return <section className="graduate-student-workspace" aria-labelledby="graduate-completion-heading">
    <div className="graduate-student-hero"><div><p className="graduate-student-eyebrow">Graduate application #{applicationId}</p>
      <h1 id="graduate-completion-heading">Capstone and faculty approvals</h1>
      <p>Enter your capstone information and select the faculty who will approve your application.</p></div></div>
    {error && <Alert variant="danger" role="alert">{error}{!form && <Button size="sm" className="ms-2" onClick={() => setRetry(value => value + 1)}>Retry</Button>}</Alert>}
    {!form && !error && <p role="status">Loading capstone and faculty…</p>}
    {form && <>
      {!canWrite && <Alert variant="info">This impersonation session is read-only. To enter capstone details and faculty approvers, stop impersonation and start a new session in Edit mode.</Alert>}
      {canWrite && !form.canEdit && <Alert variant="info">This application cannot be edited in its current status.</Alert>}
      {!editable && <div className="graduate-student-panel">
        <h2>Capstone</h2><dl className="graduate-student-details">
          <div><dt>Type</dt><dd>{capstoneTypes.find(item => item.id === form.capstone?.type)?.label || 'Not entered'}</dd></div>
          {form.capstone?.type !== 0 && <div><dt>{form.capstone?.type === 3 ? 'Other type' : 'Title'}</dt><dd>{form.capstone?.type === 3 ? form.capstone?.details : form.capstone?.title || 'Not entered'}</dd></div>}
          {form.capstone?.approvalNumber && <div><dt>IRB or IACUC approval number</dt><dd>{form.capstone.approvalNumber}</dd></div>}
        </dl><h2 className="h4 mt-4">Faculty approvers</h2><dl className="graduate-student-details">
          <div><dt>Advisor / Committee Chair</dt><dd>{facultyName(form.advisorFacultyId, form.advisorName)}{form.advisorSigned && ' · Approved'}</dd></div>
          <div><dt>Graduate Program Coordinator</dt><dd>{facultyName(form.coordinatorFacultyId, form.coordinatorName)}{form.coordinatorSigned && ' · Approved'}</dd></div>
        </dl></div>}
      {editable && <Form ref={completionForm} onSubmit={save} noValidate className="graduate-student-form graduate-completion-form">
        <section className="graduate-student-panel mb-3" aria-labelledby="capstone-fields-heading">
          <h2 id="capstone-fields-heading">Capstone details</h2>
          <p>A capstone option, Advisor / Committee Chair, and Graduate Program Coordinator are required before finishing this step.
            Select the type of work you are completing and enter its title or description.</p>
          <Form.Group controlId="capstone-type" className="mb-3"><Form.Label>Capstone type</Form.Label>
            <Form.Select ref={capstoneTypeInput} required value={type ?? ''} isInvalid={Boolean(fieldErrors.type)}
              aria-invalid={Boolean(fieldErrors.type)} aria-describedby={fieldErrors.type ? 'capstone-type-error' : undefined}
              onChange={e => { setType(e.target.value === '' ? null : Number(e.target.value));
                setFieldErrors(current => ({ ...current, type: undefined, title: undefined, details: undefined })); }}>
              <option value="">Select a type</option>
              {capstoneTypes.filter(item => item.id !== 1 || form.applicationType === 0).map(item =>
                <option key={item.id} value={item.id}>{item.label}</option>)}</Form.Select>
            {fieldErrors.type && <Form.Control.Feedback type="invalid" id="capstone-type-error">{fieldErrors.type}</Form.Control.Feedback>}</Form.Group>
          {(type === 1 || type === 2) && <Form.Group controlId="capstone-title" className="mb-3"><Form.Label>Title</Form.Label>
            <Form.Control required maxLength={200} value={title} isInvalid={Boolean(fieldErrors.title)} aria-invalid={Boolean(fieldErrors.title)}
              aria-describedby={fieldErrors.title ? 'capstone-title-error' : undefined} onChange={e => { setTitle(e.target.value); clearFieldError('title'); }} />
            {fieldErrors.title && <Form.Control.Feedback type="invalid" id="capstone-title-error">{fieldErrors.title}</Form.Control.Feedback>}</Form.Group>}
          {type === 3 && <Form.Group controlId="capstone-details" className="mb-3"><Form.Label>Other capstone type or details</Form.Label>
            <Form.Control required maxLength={200} value={details} isInvalid={Boolean(fieldErrors.details)} aria-invalid={Boolean(fieldErrors.details)}
              aria-describedby={fieldErrors.details ? 'capstone-details-error' : undefined} onChange={e => { setDetails(e.target.value); clearFieldError('details'); }} />
            {fieldErrors.details && <Form.Control.Feedback type="invalid" id="capstone-details-error">{fieldErrors.details}</Form.Control.Feedback>}</Form.Group>}
          {(type === 0 || type === 1) && <Form.Group controlId="capstone-approval" className="mb-3">
            <Form.Label>IRB or IACUC approval number <span className="graduate-optional">(if applicable)</span></Form.Label>
            <Form.Control maxLength={10} value={approval} onChange={e => setApproval(e.target.value)} /></Form.Group>}
        </section>
        <section className="graduate-student-panel mb-3" aria-labelledby="faculty-fields-heading">
          <h2 id="faculty-fields-heading">Faculty approvers</h2>
          {adminMode && <Alert variant="info">Reassigning an approved faculty member keeps the previous signature in history and requires approval from the replacement.</Alert>}
          <p>Both faculty approvers are required. Select an Advisor / Committee Chair and a Graduate Program Coordinator from the graduate faculty roster.{' '}
            If you need to check a name, see the <a href="https://grad.mnsu.edu/graduate-faculty-staff-resources/" target="_blank" rel="noreferrer">Graduate Faculty Roster</a>.</p>
          {form.faculty.length === 0 && <Alert variant="warning">No graduate faculty are available. Contact Graduate Studies.</Alert>}
          <div className="graduate-student-fields">
            <Form.Group controlId="graduate-advisor"><Form.Label>Advisor / Committee Chair</Form.Label>
              {form.advisorSigned && !adminMode ? <><p className="mb-1">{facultyName(form.advisorFacultyId, form.advisorName)} · Approved</p>
                <Form.Text>This approval is signed and cannot be reassigned.</Form.Text></> :
              <FacultyAutosuggest id="graduate-advisor" faculty={form.faculty} departmentId={form.departmentId} selectedId={advisor}
                savedName={form.advisorName} error={fieldErrors.advisor} onSelect={value => { setAdvisor(value); clearFieldError('advisor'); }} />}</Form.Group>
            <Form.Group controlId="graduate-coordinator"><Form.Label>Graduate Program Coordinator</Form.Label>
              {form.coordinatorSigned && !adminMode ? <><p className="mb-1">{facultyName(form.coordinatorFacultyId, form.coordinatorName)} · Approved</p>
                <Form.Text>This approval is signed and cannot be reassigned.</Form.Text></> :
              <FacultyAutosuggest id="graduate-coordinator" faculty={form.faculty} departmentId={form.departmentId} selectedId={coordinator}
                savedName={form.coordinatorName} error={fieldErrors.coordinator} onSelect={value => { setCoordinator(value); clearFieldError('coordinator'); }} />}</Form.Group>
          </div>
        </section>
        <div className="graduate-student-actions"><Button type="submit" disabled={busy || form.faculty.length === 0}>{busy ? 'Saving…' : 'Save and finish application'}</Button>
          <Button variant="outline-primary" type="button" disabled={busy} onClick={onEditApplication}>Edit application</Button>
          {onEditCourses && <Button variant="outline-primary" type="button" disabled={busy} onClick={onEditCourses}>Edit courses</Button>}</div>
      </Form>}
      {!editable && <div className="graduate-student-actions">
        {canWrite && form.canEdit && <Button onClick={() => setEditing(true)}>Edit capstone and approvers</Button>}
        {canWrite && form.canEdit && <Button variant="outline-primary" onClick={onEditApplication}>Edit application</Button>}
        {canWrite && form.canEdit && onEditCourses && <Button variant="outline-primary" onClick={onEditCourses}>Edit courses</Button>}
        <Button variant="outline-secondary" onClick={() => onDone(false)}>Back to applications</Button>
      </div>}
    </>}
  </section>;
}
